
import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Joyride, { STATUS } from 'react-joyride';
import { useSelector, useDispatch } from 'react-redux';
import { setStepIndex, startTour, stopTour, resetTour } from '../../store/slices/onboardingTour';
import { StorageManager } from '../../utils/storageManager';
import { StepOneContent, StepTwoContent, StepThreeContent, StepFourContent, StepFiveContent, StepSixContent, StepSevenContent } from './Steps';
import { openConfirmationModal } from '../../store/slices/confirmationModal';
import { openDrawer } from '../../store/slices/menu';
import useConfig from '../../hooks/useConfig';
import { useTheme } from '@mui/material/styles';
import { updateUserData } from '../../store/slices/user';

const DashboardTour = () => {
    const theme = useTheme();
    const mode = theme.palette.mode;

    const dispatch = useDispatch();
    const { matchDownMd } = useConfig();
    const { run, isReady, stepIndex } = useSelector((state) => state.onboardingTour);

    const navigate = useNavigate();
    const location = useLocation();

    const tourSteps = [
        {
            target: '.buy_number_button',
            content: (
                <StepTwoContent />
            ),
            data: {
                id: 0,
                previous: '',
                next: '/dashboard/numbers/buy',
                isLastStep: false,
            },
            disableBeacon: true,

        },
        {
            target: '.country_filter_search_bar',
            content: (
                <StepThreeContent />
            ),
            data: {
                id: 1,
                previous: '/dashboard/numbers',
                next: '/dashboard/numbers',
                isLastStep: false,
            },
            disableBeacon: true,

        },
        {
            target: '.phone_number_table',
            content: (
                <StepOneContent />
            ),
            data: {
                id: 2,
                previous: '/dashboard/numbers/buy',
                next: '',
                isLastStep: false,
            },
            disableBeacon: true,
        },
        {
            target: '.assistants_tab',
            content: (
                <StepFourContent />
            ),
            data: {
                id: 3,
                previous: '/dashboard/numbers',
                next: '/dashboard/assistants/other',
                isLastStep: false,
            },
            disableBeacon: true,

        },
        {
            target: '.create_agent_button',
            content: (
                <StepFiveContent />
            ),
            data: {
                id: 4,
                previous: '/dashboard/numbers',
                next: '/dashboard/numbers',
                isLastStep: false,
            },
            disableBeacon: true,

        },
        {
            target: '.number_setting_button',
            content: (
                <StepSixContent />
            ),
            data: {
                id: 5,
                previous: '/dashboard/assistants/other',
                next: '/dashboard/numbers/66eb0ad03106765c0f05ef3d',
                isLastStep: false,
            },
            disableBeacon: true,

        },
        {
            target: '.assign_agent',
            content: (
                <StepSevenContent />
            ),
            data: {
                id: 6,
                previous: '/dashboard/numbers',
                next: '',
                isLastStep: true,
            },
            disableBeacon: true,
        },
    ];

    const checkTargetExists = useCallback((target) => {
        return document.querySelector(target) !== null;
    }, []);

    const saveUserData = async (values) => {
        try {
            await dispatch(updateUserData({
                data: values,
                show_message: false,
            }))
        }
        catch (error) {
            console.log(error);
        }
    }

    const handleNavigation = () => {
        //open the drawer in small devices when the step index is 0 or 3 because the content is inside the drawer.
        if (matchDownMd) {
            if ([3].includes(stepIndex)) {
                dispatch(openDrawer(true));
            }
            else {
                dispatch(openDrawer(false));
            }
        }

        const currentStep = tourSteps[stepIndex];
        if (!currentStep) return;
        dispatch(stopTour());

        const checkTarget = setInterval(() => {
            if (currentStep.target && document.querySelector(currentStep.target)) {
                if (isReady) {
                    dispatch(startTour());
                }
                clearInterval(checkTarget);
            }
        }, 500);

        return () => clearInterval(checkTarget);
    }

    useEffect(() => {
        if (run) return; // Prevent running if already ready or running
        handleNavigation();
    }, [location.pathname, run, isReady, stepIndex]);


    const handleJoyrideCallback = async (data) => {

        const { action, status, step, type } = data;
        const { id, previous, next, isLastStep } = step.data;

        if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            dispatch(resetTour());
            return;
        }

        const isPreviousAction = action === 'prev';
        const newStepIndex = isPreviousAction ? stepIndex - 1 : stepIndex + 1;

        // Determine if the step is the last one based on `step:after` and `next`
        if (type === 'step:after' && isLastStep && !step.data.next) {
            dispatch(resetTour())
            await new Promise(resolve => setTimeout(resolve, 500))
            dispatch(
                openConfirmationModal({
                    open: true,
                    message: "Welcome to RingUs! Let's get you started with a quick tutorial.",
                    modalType: "ONBOARDING_TOUR_END_MODAL",
                    closable: false,
                    handleConfirm: async () => {
                        navigate('/dashboard/quick-start')
                    },
                })
            );
            await saveUserData({ tour_completed: true })
            return;
        }

        if (type === 'step:after') {
            dispatch(stopTour());
            dispatch(setStepIndex(newStepIndex));

            if (isPreviousAction && previous) {
                navigate(previous);
            }
            else if (!isPreviousAction && next) {
                navigate(next);
            }
        }
    };

    //if joyride is not running.
    if (!run) return null;

    return (
        <Joyride
            steps={tourSteps}
            stepIndex={stepIndex}
            run={run}
            continuous={true}
            showProgress={true}
            showSkipButton={true}
            disableCloseOnEsc={true}
            disableOverlayClose={true}
            callback={handleJoyrideCallback}
            locale={{
                last: 'Finish',
                next: 'Next',
                skip: 'Skip',
                back: 'Back',
            }}
            styles={{
                options: {
                    arrowColor: '#eee',
                    backgroundColor: theme.palette.background.paper,
                    overlayColor: 'rgba(0, 0, 0, 0.6)',
                    primaryColor: '#169CD9',
                    textColor: theme.palette.text.primary,
                    zIndex: 6000,
                },
                spotlight: {
                    borderRadius: '8px',
                    boxShadow: `0 0 0 2px ${theme.palette.background.light}`,
                },
            }}
        />
    );
};

export default DashboardTour;

